import type { StoredFile } from '~/components/types/claim-document-types';

/**
 * Represents an uploading document structure that matches the backend's get_claim_uploading_documents format
 */
export interface UploadingDocument {
  id?: string;
  stored_file_id: number;
  document_name: string;
  is_uploading: boolean;
  stored_file?: StoredFile;
  clive_store_information?: {
    processing_status: string;
    full_summary?: string;
  };
  size?: number;
  mime_type?: string;
}

/**
 * Transforms a StoredFile into an UploadingDocument structure for Clive processing.
 *
 * This helper is used when a notification contains a stored_file but the uploading document
 * is not found in claim.uploading_documents (e.g., old notifications or edge cases).
 *
 * @param storedFile - The stored file from the notification
 * @param uploadingDocuments - Optional array of uploading documents from the claim
 * @returns UploadingDocument structure or null if storedFile is not provided
 */
export function getFailedUploadingDocumentFromStoredFile(
  storedFile: StoredFile | null | undefined,
  uploadingDocuments?: UploadingDocument[]
): UploadingDocument | null {
  if (!storedFile) {
    return null;
  }

  // Try to find from uploading_documents first
  if (uploadingDocuments) {
    const found = uploadingDocuments.find((doc) => doc.stored_file?.id === storedFile.id);
    if (found) {
      return found;
    }
  }

  const storedFileExtra = storedFile.stored_file_extra as Record<string, unknown> | null | undefined;
  const aiStatus = storedFileExtra?.ai_processing_status as string | undefined;

  // Determine processing status based on AI status
  let processingStatus: string;

  if (!aiStatus) {
    processingStatus = 'error_uploading';
  } else if (aiStatus === 'error_classifying_document_type') {
    processingStatus = 'error_classifying_document_type';
  } else if (aiStatus === 'error_unsupported_file_type') {
    processingStatus = 'error_unsupported_file_type';
  } else if (aiStatus === 'error_file_too_large') {
    processingStatus = 'error_file_too_large';
  } else {
    processingStatus = 'error_uploading';
  }

  return {
    stored_file_id: storedFile.id,
    document_name: storedFile.original_filename,
    is_uploading: true,
    stored_file: storedFile,
    clive_store_information: {
      processing_status: processingStatus,
      full_summary: 'Document classification failed',
    },
  };
}
